/**
 * A presentation type's slots, read from its own `configuration_schema`
 * (CT-5641).
 *
 * This is the single reader of the rev4 contract. Everything downstream — the
 * payload builder, the parser that reopens a saved widget, the validator, and
 * (next ticket) the binding editor — asks it what a type wants instead of
 * carrying a map keyed on `typeCode`. Guide 18 §4 states the same rule from
 * the product side: *"el tipo proporciona slots, cardinalidades, operaciones y
 * esquema"*, and the form is built from that, not from a branch per type.
 *
 * ## What a slot is
 *
 * `properties.bindings.properties.<key>`, either an object (one binding) or an
 * array of them (`y[1-8]`, `columns[1-40]`, `identity[1-2]`). The item node
 * carries the whole contract:
 *
 * | Annotation | What it decides |
 * |---|---|
 * | `x-field-kinds` | The slot's role: `AGGREGATE`, `DIMENSION`, `COLUMN`, or two of them |
 * | `x-aggregates` | Which operations a measure slot offers |
 * | `x-data-types` | Which columns may be offered for it |
 * | `x-data-types-by-aggregate` | The narrower set once an operation is chosen |
 * | `x-output-path` | Where its value lands in the shaped response |
 * | `x-param-kind` | `MEASURE`, `DIMENSION`, `ATTRIBUTE`, `TIME` |
 * | `properties` | Which keys the item may carry at all (`additionalProperties: false`) |
 *
 * ## What it deliberately does not interpret
 *
 * **Non-field slots.** Six keys declare no `x-field-kinds` — `time`,
 * `comparisons`, `point_mode`, `source_mode`, `relationships`,
 * `context_queries` — and each has a shape of its own. They are returned
 * separately, with their node, so a caller that knows one (see
 * `widgetTimeBinding`) can read it and the generic field editor never tries.
 *
 * **`formula` and `null_on_divide_by_zero`.** Two types declare them beside
 * `bindings` and `display`. Guide 17 §4 and guide 18 §6 are explicit that the
 * public authoring path does not create the CALCULATED projection they would
 * need, so nothing here reads them: a control that writes a formula the sync
 * drops is worse than no control.
 *
 * **`display`.** Read by `displaySettings`, which has rendered it since
 * CT-5579 and needs no change for rev4.
 */

import {
  NON_FIELD_BINDING_SLOTS,
  WIDGET_FIELD_ROLES,
} from '../constants/widgetContract.js';
import { aggregateOutputType, dataTypeFits } from './widgetDataTypes.js';

const EMPTY = Object.freeze([]);
const EMPTY_MODEL = Object.freeze({
  contractVersion: null,
  slots: EMPTY,
  nonFieldSlots: EMPTY,
});

/** `$ref: '#/$defs/format'` → the `$defs` node it points at. */
const resolveRef = (node, schema) => {
  if (!node?.$ref) return node;
  return schema?.$defs?.[node.$ref.split('/').pop()] ?? null;
};

/** A slot that accepts `UUID` and nothing else keys the row; see below. */
const isRecordKey = (dataTypes) => dataTypes.length === 1 && dataTypes[0] === 'UUID';

const readItem = (node) => (node?.type === 'array' ? node.items ?? {} : node ?? {});

/**
 * One slot's model.
 *
 * `supports` is the item's own property list, which is what tells a caller
 * whether this slot takes a `label`, a `time_grain` or a `width_px` — the
 * standard keys are not on every slot, and `additionalProperties: false`
 * turns a hopeful extra into a rejected payload.
 */
const readSlot = (key, node, { required, schema }) => {
  const item = readItem(node);
  const isArray = node?.type === 'array';
  const properties = item?.properties ?? {};
  const roles = item?.['x-field-kinds'] ?? EMPTY;
  const dataTypes = item?.['x-data-types'] ?? node?.['x-data-types'] ?? EMPTY;

  return {
    key,
    required,
    isArray,
    // `minItems` only binds a slot that is present: an absent optional array
    // is absent, not an empty one short of its minimum (guide 12 §4, fixed by
    // corrective Phase 1).
    min: isArray ? node.minItems ?? 0 : null,
    max: isArray ? node.maxItems ?? null : null,
    roles,
    aggregates: item?.['x-aggregates'] ?? properties.aggregate?.enum ?? EMPTY,
    dataTypes,
    dataTypesByAggregate: item?.['x-data-types-by-aggregate'] ?? null,
    /** The keys this item may carry — the author's whole vocabulary for it. */
    supports: Object.freeze(Object.keys(properties)),
    /** Of those, the ones the item cannot be sent without. */
    requiredKeys: Object.freeze([...(item?.required ?? EMPTY)]),
    /** `$defs`-resolved `format` node, for the control that will render it. */
    formatSchema: resolveRef(properties.format, schema),
    outputPath: item?.['x-output-path'] ?? node?.['x-output-path'] ?? null,
    paramKind: item?.['x-param-kind'] ?? node?.['x-param-kind'] ?? null,
    /*
      There is exactly one candidate for a `UUID`-only slot — the entity's
      `<base>_uuid` — so it is assigned rather than asked. Marked here so the
      editor does not render a question with one answer, and so the payload
      builder knows which slot to put the record key in.
    */
    isRecordKey: isRecordKey(dataTypes),
  };
};

/**
 * Read a type's bindings contract.
 *
 * @param {object|null} configSchema The type's `configuration_schema`, as
 *   `widget/type/list` publishes it (`configSchema` on the mapped type).
 * @returns {{
 *   contractVersion: number|null,
 *   slots: Array<object>,
 *   nonFieldSlots: Array<{ key: string, required: boolean, node: object }>,
 * }} Slots in schema order. An unseeded or unreadable type yields empty
 *   lists, which is what makes the callers refuse to build a contract for it
 *   rather than build one from a guess.
 */
export function readSlotModel(configSchema) {
  const bindings = configSchema?.properties?.bindings;
  const properties = bindings?.properties;
  if (!properties) return EMPTY_MODEL;

  const required = new Set(bindings.required ?? EMPTY);
  const slots = [];
  const nonFieldSlots = [];

  Object.entries(properties).forEach(([key, node]) => {
    const item = readItem(node);
    const isFieldSlot = Boolean(item?.['x-field-kinds']?.length);
    if (!isFieldSlot || NON_FIELD_BINDING_SLOTS.includes(key)) {
      nonFieldSlots.push({ key, required: required.has(key), node });
      return;
    }
    slots.push(readSlot(key, node, { required: required.has(key), schema: configSchema }));
  });

  return {
    contractVersion: configSchema?.properties?.contract_version?.const ?? null,
    slots: Object.freeze(slots),
    nonFieldSlots: Object.freeze(nonFieldSlots),
  };
}

/**
 * The item keys every slot has in common, whatever its own list says.
 *
 * `supports` is read from the item's `properties`, and a schema that states
 * none tells us nothing about its vocabulary — an older catalog row, or a
 * fixture written before rev4. Censoring `field` there would quietly emit a
 * binding with no column in it, so the standard seven are assumed and only
 * the slot-specific extras are withheld.
 */
const STANDARD_ITEM_KEYS = new Set([
  'field', 'field_key', 'aggregate', 'time_grain', 'date_part', 'label', 'format',
]);

/** May this slot's item carry this key? */
export const slotSupports = (slot, key) => (slot?.supports?.length
  ? slot.supports.includes(key)
  : STANDARD_ITEM_KEYS.has(key));

/** The slot a key names, or `null`. */
export const slotFor = (model, key) => model?.slots?.find((slot) => slot.key === key) ?? null;

/** Does this slot accept that role? A slot with two accepts either. */
export const slotAcceptsRole = (slot, role) => Boolean(slot?.roles?.includes(role));

/** A measure slot takes an `aggregate`; a dimension or a column must not. */
export const slotTakesAggregate = (slot) =>
  slotAcceptsRole(slot, WIDGET_FIELD_ROLES.AGGREGATE) && slotSupports(slot, 'aggregate');

/**
 * The **source** columns this slot accepts, narrowed by the chosen operation.
 *
 * `x-data-types-by-aggregate` is the authority once an aggregate is picked —
 * `SUM` refuses the `TEXT` that `COUNT` is happy with — and the slot's own
 * `x-data-types` answers before one is.
 *
 * `COUNT_ROWS` maps to an **empty** list there, and that is not "anything
 * goes": it reads no column at all, so there is no source type to check.
 *
 * This is one half of fitting a column to a measure slot. The other half is
 * what the operation *produces* — see `columnFitsSlotModel`.
 */
export function acceptedSourceTypes(slot, aggregate = '') {
  const byAggregate = slot?.dataTypesByAggregate;
  const narrowed = aggregate && byAggregate && Object.hasOwn(byAggregate, aggregate)
    ? byAggregate[aggregate]
    : null;
  if (narrowed?.length) return narrowed;
  return slot?.dataTypes ?? EMPTY;
}

/**
 * Can this column fill this slot, under this operation?
 *
 * Both halves, because the backend checks both: the column has to be one the
 * operation can read, and what the operation produces has to be a type the
 * slot accepts. Missing the second is how an `INTEGER` column reached
 * `COST_KPI.amount` and failed at publish (see `aggregateOutputType`).
 *
 * @param {{ dataType?: string }} column A loaded source column.
 * @param {object} slot From `readSlotModel`.
 * @param {string} [aggregate] The operation the author chose, if any.
 */
export const columnFitsSlotModel = (column, slot, aggregate = '') =>
  dataTypeFits(column?.dataType, acceptedSourceTypes(slot, aggregate))
  && dataTypeFits(aggregateOutputType(aggregate, column?.dataType), slot?.dataTypes ?? EMPTY);

export default readSlotModel;
